import React, { useEffect, useState, useMemo } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Plus, Pencil, Trash2, X, Save, ImageIcon, Star, Award, MapPin, CheckCircle2, Sparkles, Building2, User, Target, Wand2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import GooglePreview from "./GooglePreview";
import ImageUploader from "./ImageUploader";
import RichEditor from "../../components/admin/RichEditor";

const empty = {
  title: "",
  description: "",
  category: "Climatisation",
  intervention_type: "Installation",
  images: [],
  youtube_url: "",
  zone: "",
  postal_code: "",
  date: "",
  duration: "",
  client_type: "particulier",
  brand: "",
  model: "",
  objective: "",
  solution: "",
  benefits: [],
  surface: "",
  budget_range: "",
  featured: false,
  meta_title: "",
  meta_description: "",
  slug: "",
  keyword: "",
};

const CATEGORIES = ["Climatisation", "Climatisation gainable", "Pompe à chaleur", "Plomberie", "Dépannage", "Entretien", "Rénovation", "Chauffe-eau"];
const INTERVENTIONS = ["Installation", "Dépannage", "Entretien", "Rénovation", "Remplacement", "Mise aux normes"];
const CLIENT_TYPES = [
  { value: "particulier", label: "Particulier", Icon: User },
  { value: "professionnel", label: "Professionnel", Icon: Building2 },
  { value: "entreprise", label: "Entreprise", Icon: Building2 },
  { value: "collectivite", label: "Collectivité", Icon: Building2 },
];
const BRANDS = ["DAIKIN", "MITSUBISHI", "HITACHI", "HEIWA", "ATLANTIC", "PANASONIC", "LG", "SAMSUNG", "TOSHIBA", "AUTRE"];

function scoreSEO(data) {
  let score = 0; const tips = [];
  const kw = (data.keyword || "").toLowerCase().trim();
  if (data.meta_title && data.meta_title.length >= 30 && data.meta_title.length <= 65) score += 15; else tips.push("Meta title 30-65 caractères");
  if (data.meta_description && data.meta_description.length >= 120 && data.meta_description.length <= 165) score += 15; else tips.push("Meta description 120-165 caractères");
  if (data.description && data.description.length >= 300) score += 10; else tips.push("Description riche (≥ 300 car.)");
  if (data.images?.length >= 3) score += 10; else tips.push("Ajouter 3+ photos");
  if (data.zone && data.postal_code) score += 10; else tips.push("Ville + code postal");
  if (data.objective && data.solution) score += 10; else tips.push("Objectif + solution");
  if (data.benefits?.length >= 3) score += 10; else tips.push("Au moins 3 bénéfices");
  if (kw) {
    const kwHits = ["meta_title", "meta_description", "description", "title"].filter(k => (data[k] || "").toLowerCase().includes(kw)).length;
    if (kwHits >= 3) score += 10; else tips.push(`Utiliser "${data.keyword}" dans 3+ champs`);
  } else tips.push("Définir un mot-clé cible");
  if (data.brand) score += 5;
  if (data.intervention_type && data.category) score += 5;
  return { score: Math.min(100, score), tips };
}

export default function RealisationsAdmin() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => setItems((await api.get("/realizations")).data);
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!edit.title) return toast.error("Le titre est obligatoire");
    setSaving(true);
    try {
      const body = {
        ...edit,
        images: Array.isArray(edit.images) ? edit.images : (edit.images || "").split("\n").map(x => x.trim()).filter(Boolean),
        benefits: Array.isArray(edit.benefits) ? edit.benefits : (edit.benefits || "").split("\n").map(x => x.trim()).filter(Boolean),
      };
      if (edit.id) await api.put(`/realizations/${edit.id}`, body);
      else await api.post("/realizations", body);
      toast.success("Enregistré");
      setEdit(null); load();
    } catch (e) { toast.error("Erreur d'enregistrement"); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-6 md:p-10" data-testid="admin-realisations-page">
      <header className="flex items-center justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brand-sky)] font-semibold mb-1">CMS · SEO Premium</div>
          <h1 className="font-display text-3xl font-bold text-[color:var(--brand-navy)]">Réalisations</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Chaque fiche est optimisée pour le référencement local.</p>
        </div>
        <Button onClick={() => setEdit({ ...empty })} className="bg-[color:var(--brand-red)]" data-testid="new-realisation-btn">
          <Plus className="w-4 h-4 mr-1" />Nouvelle réalisation
        </Button>
      </header>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(r => {
          const seo = r.seo?.score ?? 0;
          return (
            <Card key={r.id} className="overflow-hidden border shadow-hover group">
              {r.images?.[0] && <img src={r.images[0]} className="h-44 w-full object-cover" alt={r.title} />}
              <div className="p-4">
                <div className="flex items-center gap-2 flex-wrap text-[10px] uppercase tracking-wider font-bold mb-2">
                  {r.category && <span className="px-2 py-0.5 rounded-full bg-[color:var(--brand-sky)]/10 text-[color:var(--brand-sky)]">{r.category}</span>}
                  {r.intervention_type && <span className="px-2 py-0.5 rounded-full bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]">{r.intervention_type}</span>}
                  {r.featured && <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 flex items-center gap-0.5"><Star className="w-2.5 h-2.5" /> Mise en avant</span>}
                </div>
                <h3 className="font-display font-bold text-[color:var(--brand-navy)] mb-1 line-clamp-2">{r.title}</h3>
                {r.zone && <div className="text-xs text-[hsl(var(--muted-foreground))] mb-2 flex items-center gap-1"><MapPin className="w-3 h-3" />{r.zone}{r.postal_code && ` · ${r.postal_code}`}</div>}
                <div className="flex items-center justify-between mt-3">
                  <span className={`inline-block px-2 py-0.5 rounded-full font-mono font-bold text-[10px] ${seo >= 80 ? "bg-emerald-100 text-emerald-700" : seo >= 50 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                    SEO {seo}
                  </span>
                  <div className="flex gap-1">
                    <Button variant="outline" size="sm" onClick={() => setEdit(r)} data-testid={`edit-real-${r.id}`}><Pencil className="w-3 h-3" /></Button>
                    <Button variant="outline" size="sm" onClick={async () => {
                      if (!window.confirm("Supprimer cette réalisation ?")) return;
                      await api.delete(`/realizations/${r.id}`); load();
                    }}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {edit && <RealisationEditor edit={edit} setEdit={setEdit} save={save} saving={saving} />}
    </div>
  );
}

function RealisationEditor({ edit, setEdit, save, saving }) {
  const seo = useMemo(() => scoreSEO({
    ...edit,
    images: Array.isArray(edit.images) ? edit.images : (edit.images || "").split("\n").filter(Boolean),
    benefits: Array.isArray(edit.benefits) ? edit.benefits : (edit.benefits || "").split("\n").filter(Boolean),
  }), [edit]);

  const set = (k, v) => setEdit({ ...edit, [k]: v });

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-2 md:p-4 overflow-y-auto" onClick={() => setEdit(null)} data-testid="real-editor-modal">
      <div className="bg-white rounded-2xl w-full max-w-4xl my-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b">
          <div>
            <h3 className="font-display font-bold text-xl text-[color:var(--brand-navy)]">{edit.id ? "Éditer" : "Nouvelle"} réalisation</h3>
            <p className="text-xs text-[hsl(var(--muted-foreground))]">Fiche SEO premium — remplissez tous les champs pour un meilleur référencement.</p>
          </div>
          <button onClick={() => setEdit(null)}><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-5 max-h-[75vh] overflow-y-auto">
          {/* SEO SCORE BANNER */}
          <div className={`rounded-2xl p-4 flex items-center gap-4 ${seo.score >= 80 ? "bg-emerald-50 border border-emerald-200" : seo.score >= 50 ? "bg-amber-50 border border-amber-200" : "bg-red-50 border border-red-200"}`}>
            <div className={`w-16 h-16 rounded-full flex items-center justify-center font-display font-black text-xl ${seo.score >= 80 ? "bg-emerald-500 text-white" : seo.score >= 50 ? "bg-amber-500 text-white" : "bg-red-500 text-white"}`}>
              {seo.score}
            </div>
            <div className="flex-1">
              <div className="font-display font-bold text-[color:var(--brand-navy)]">Score SEO en temps réel</div>
              {seo.tips.length > 0 ? (
                <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                  <strong>Pour progresser :</strong> {seo.tips.slice(0, 3).join(" · ")}
                  {seo.tips.length > 3 && ` · +${seo.tips.length - 3} autres`}
                </div>
              ) : <div className="text-xs text-emerald-700 mt-1 font-semibold">✓ Excellente fiche, prête à ranker.</div>}
            </div>
          </div>

          {/* SECTION: Base */}
          <Section title="Identification" icon={Sparkles}>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="md:col-span-2"><Label>Titre de la réalisation *</Label><Input value={edit.title} onChange={e => set("title", e.target.value)} placeholder="Ex: Installation clim gainable DAIKIN chez M. Dupont à Grans" /></div>
              <div><Label>Catégorie service</Label>
                <Select value={edit.category} onValueChange={v => set("category", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Type d'intervention</Label>
                <Select value={edit.intervention_type} onValueChange={v => set("intervention_type", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{INTERVENTIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Mot-clé SEO principal</Label><Input value={edit.keyword} onChange={e => set("keyword", e.target.value)} placeholder="Ex: climatisation gainable Grans" /></div>
              <div><Label>Featured (homepage)</Label>
                <div className="flex items-center gap-2 mt-2">
                  <input type="checkbox" checked={!!edit.featured} onChange={e => set("featured", e.target.checked)} data-testid="real-featured" />
                  <span className="text-sm">Mettre en avant sur la galerie</span>
                </div>
              </div>
            </div>
          </Section>

          {/* SECTION: Localisation */}
          <Section title="Localisation (SEO local)" icon={MapPin}>
            <div className="grid md:grid-cols-3 gap-3">
              <div className="md:col-span-2"><Label>Ville</Label><Input value={edit.zone} onChange={e => set("zone", e.target.value)} placeholder="Grans" /></div>
              <div><Label>Code postal</Label><Input value={edit.postal_code} onChange={e => set("postal_code", e.target.value)} placeholder="13450" maxLength={5} /></div>
            </div>
          </Section>

          {/* SECTION: Projet */}
          <Section title="Détails projet" icon={Target}>
            <div className="grid md:grid-cols-2 gap-3">
              <div><Label>Type client</Label>
                <div className="grid grid-cols-2 gap-2 mt-1.5">
                  {CLIENT_TYPES.map(c => (
                    <button
                      key={c.value}
                      type="button"
                      onClick={() => set("client_type", c.value)}
                      className={`flex items-center gap-1.5 px-2 py-2 rounded-xl border text-xs font-semibold ${edit.client_type === c.value ? "border-[color:var(--brand-sky)] bg-[color:var(--brand-sky)]/10 text-[color:var(--brand-navy)]" : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]"}`}
                    ><c.Icon className="w-3 h-3" />{c.label}</button>
                  ))}
                </div>
              </div>
              <div><Label>Date réalisation</Label><Input type="date" value={edit.date?.slice(0, 10) || ""} onChange={e => set("date", e.target.value)} /></div>
              <div><Label>Durée chantier</Label><Input value={edit.duration} onChange={e => set("duration", e.target.value)} placeholder="2 jours" /></div>
              <div><Label>Surface traitée</Label><Input value={edit.surface} onChange={e => set("surface", e.target.value)} placeholder="120 m²" /></div>
              <div><Label>Marque matériel</Label>
                <Select value={edit.brand || undefined} onValueChange={v => set("brand", v)}>
                  <SelectTrigger><SelectValue placeholder="Choisir..." /></SelectTrigger>
                  <SelectContent>{BRANDS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Modèle</Label><Input value={edit.model} onChange={e => set("model", e.target.value)} placeholder="Ex: Emura FTXM35R" /></div>
              <div className="md:col-span-2"><Label>Gamme budget (indicative)</Label><Input value={edit.budget_range} onChange={e => set("budget_range", e.target.value)} placeholder="Ex: 3 500 € – 5 000 €" /></div>
            </div>
          </Section>

          {/* SECTION: Récit */}
          <Section title="Récit client" icon={Award}>
            <div className="space-y-3">
              <div><Label>Objectif du projet (pourquoi le client nous a appelés)</Label>
                <Textarea rows={3} value={edit.objective} onChange={e => set("objective", e.target.value)} placeholder="Rafraîchir toute la maison en évitant les unités visibles en façade, pour préserver l'esthétique." />
              </div>
              <div><Label>Solution apportée</Label>
                <Textarea rows={3} value={edit.solution} onChange={e => set("solution", e.target.value)} placeholder="Installation d'un système gainable DAIKIN 7 kW avec 4 bouches de soufflage invisibles dans les faux-plafonds." />
              </div>
              <div><Label>Bénéfices client (une ligne par bénéfice)</Label>
                <Textarea rows={5} value={Array.isArray(edit.benefits) ? edit.benefits.join("\n") : edit.benefits} onChange={e => set("benefits", e.target.value)}
                  placeholder={"0 unité intérieure visible\nConsommation divisée par 3 vs ancien clim mobile\nGarantie décennale + matériel 5 ans\nRéversible chaud / froid"} />
              </div>
              <div><Label>Description longue (récit complet SEO)</Label>
                <p className="text-[11px] text-[hsl(var(--muted-foreground))] mb-1.5">Racontez le chantier de A à Z : diagnostic, contraintes, choix techniques, installation, résultat. Éditeur WYSIWYG complet.</p>
                <RichEditor value={edit.description} onChange={(html) => set("description", html)} placeholder="Racontez le chantier : diagnostic, contraintes, choix techniques, installation, résultat…" minHeight={300} />
              </div>
            </div>
          </Section>

          {/* SECTION: Média */}
          <Section title="Médias" icon={ImageIcon}>
            <div className="space-y-3">
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <Label>Images (une URL par ligne — min 3 recommandé)</Label>
                  <ImageUploader
                    folder="realisations"
                    label="Ajouter plusieurs photos"
                    multiple
                    onUploaded={(url) => {
                      setEdit((prev) => {
                        const current = Array.isArray(prev.images) ? prev.images : (prev.images || "").split("\n").filter(Boolean);
                        return { ...prev, images: [...current, url] };
                      });
                    }}
                  />
                </div>
                <Textarea rows={4} value={Array.isArray(edit.images) ? edit.images.join("\n") : edit.images} onChange={e => set("images", e.target.value)}
                  placeholder={"/uploads/cms/realisations/xxx.webp\n..."} />
                {Array.isArray(edit.images) && edit.images.length > 0 && (
                  <div className="mt-2 flex gap-1.5 flex-wrap">
                    {edit.images.slice(0, 8).map((u, i) => <img key={i} src={u} className="h-14 w-14 object-cover rounded-md border" alt="" />)}
                  </div>
                )}
              </div>
              <div><Label>YouTube ID (optionnel)</Label><Input value={edit.youtube_url} onChange={e => set("youtube_url", e.target.value)} placeholder="dQw4w9WgXcQ" /></div>
            </div>
          </Section>

          {/* SECTION: SEO */}
          <Section title="SEO meta & preview" icon={Wand2}>
            <div className="space-y-3">
              <div><Label>Meta title (30-65 car · actuellement {(edit.meta_title || "").length})</Label><Input value={edit.meta_title} onChange={e => set("meta_title", e.target.value)} placeholder="Installation clim gainable DAIKIN à Grans — KLOUB'AIR" /></div>
              <div><Label>Meta description (120-165 car · actuellement {(edit.meta_description || "").length})</Label><Textarea rows={2} value={edit.meta_description} onChange={e => set("meta_description", e.target.value)} /></div>
              <div><Label>Slug URL</Label><Input value={edit.slug} onChange={e => set("slug", e.target.value)} placeholder="clim-gainable-daikin-grans" /></div>
              <GooglePreview title={edit.meta_title || edit.title} description={edit.meta_description} slug={edit.slug} path="realisations" />
            </div>
          </Section>
        </div>

        <div className="flex justify-end gap-2 p-5 border-t">
          <Button variant="outline" onClick={() => setEdit(null)}>Annuler</Button>
          <Button onClick={save} disabled={saving} className="bg-[color:var(--brand-navy)] font-bold" data-testid="save-real">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Enregistrement...</> : <><Save className="w-4 h-4 mr-1" />Enregistrer</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="rounded-2xl border border-[hsl(var(--border))] p-4">
      <div className="flex items-center gap-2 text-[color:var(--brand-navy)] font-display font-bold mb-3">
        <Icon className="w-4 h-4 text-[color:var(--brand-sky)]" />
        {title}
      </div>
      {children}
    </div>
  );
}
